/**
 * Formatting and status vocabulary.
 *
 * The status→tone mapping is the only place hue is chosen. Every consumer renders
 * a label alongside it, so state is never communicated by colour alone.
 */

export type Tone = 'good' | 'warning' | 'serious' | 'critical' | 'neutral' | 'info';

const DATE_FMT = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const DATE_SHORT_FMT = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short' });

const DATE_TIME_FMT = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return DATE_FMT.format(new Date(value));
}

export function formatDateShort(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return DATE_SHORT_FMT.format(new Date(value));
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return DATE_TIME_FMT.format(new Date(value));
}

/** "3 days ago", "in 5 days", "today". */
export function relativeDays(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const target = new Date(value);
  const days = Math.round((startOfDay(target) - startOfDay(new Date())) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${days} days` : `${Math.abs(days)} days ago`;
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export function titleCase(value: string | null | undefined): string {
  if (!value) return '—';
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// ---------------------------------------------------------------------------
// Status vocabulary
// ---------------------------------------------------------------------------

export const HEALTH_TONE: Record<string, Tone> = {
  green: 'good',
  yellow: 'warning',
  red: 'critical',
  unknown: 'neutral',
};

export const HEALTH_LABEL: Record<string, string> = {
  green: 'Green',
  yellow: 'Yellow',
  red: 'Red',
  unknown: 'Not rated',
};

export const IMPORTANCE_TONE: Record<string, Tone> = {
  strategic: 'info',
  high: 'neutral',
  standard: 'neutral',
  project: 'neutral',
};

export const READINESS_TONE: Record<string, Tone> = {
  unknown: 'neutral',
  on_track: 'good',
  at_risk: 'serious',
  blocked: 'critical',
  complete: 'good',
};

export const READINESS_LABEL: Record<string, string> = {
  unknown: 'Not confirmed',
  on_track: 'On track',
  at_risk: 'At risk',
  blocked: 'Blocked',
  complete: 'Complete',
};

export const PRIORITY_TONE: Record<string, Tone> = {
  critical: 'critical',
  high: 'serious',
  medium: 'neutral',
  low: 'neutral',
};

export const WORK_ITEM_STATUS_TONE: Record<string, Tone> = {
  open: 'neutral',
  in_progress: 'info',
  waiting_on_customer: 'warning',
  blocked: 'critical',
  completed: 'good',
  cancelled: 'neutral',
};

export const CADENCE_TONE: Record<string, Tone> = {
  on_cadence: 'good',
  due_soon: 'warning',
  behind: 'critical',
  not_configured: 'neutral',
};

export const CADENCE_LABEL: Record<string, string> = {
  on_cadence: 'On cadence',
  due_soon: 'Due soon',
  behind: 'Behind',
  not_configured: 'No cadence set',
};

export const WAITING_STATUS_TONE: Record<string, Tone> = {
  waiting_on_customer: 'warning',
  customer_response_detected: 'info',
  followup_due: 'serious',
  followed_up: 'neutral',
  resolved: 'good',
};

export const WAITING_STATUS_LABEL: Record<string, string> = {
  waiting_on_customer: 'Waiting on customer',
  customer_response_detected: 'Response detected',
  followup_due: 'Follow-up due',
  followed_up: 'Followed up',
  resolved: 'Resolved',
};

export const DEPENDENCY_TONE: Record<string, Tone> = {
  pending: 'neutral',
  in_progress: 'info',
  satisfied: 'good',
  blocked: 'critical',
};

export const PROJECT_STATUS_TONE: Record<string, Tone> = {
  planned: 'neutral',
  active: 'info',
  waiting_on_customer: 'warning',
  blocked: 'critical',
  completed: 'good',
  paused: 'neutral',
  cancelled: 'neutral',
};

export const ATTENTION_TONE: Record<string, Tone> = {
  critical_overdue_commitment: 'critical',
  commitment_at_risk: 'serious',
  customer_notification: 'info',
  response_sla_breach: 'critical',
  internal_readiness_check: 'warning',
  followup_overdue: 'serious',
  customer_response_detected: 'info',
  cadence_overdue: 'warning',
  commitment_due_soon: 'neutral',
  ai_proposal_review: 'info',
  biweekly_update: 'neutral',
  routine_task: 'neutral',
};

export const WORK_ITEM_TYPE_LABEL: Record<string, string> = {
  task: 'Task',
  customer_commitment: 'Commitment',
  customer_followup: 'Follow-up',
  opportunity_signal: 'Opportunity signal',
  customer_notification: 'Customer notification',
  contract_obligation: 'Contract obligation',
};

export const ASSIGNMENT_LABEL: Record<string, string> = {
  primary_csm: 'Primary CSM',
  secondary_csm: 'Secondary CSM',
  executive_sponsor: 'Executive sponsor',
  temporary_coverage: 'Temporary coverage',
  collaborator: 'Collaborator',
};

export const ACTIVITY_LABEL: Record<string, string> = {
  email_inbound: 'Email received',
  email_outbound: 'Email sent',
  meeting: 'Meeting',
  note: 'Note',
  decision: 'Decision',
  task_created: 'Task created',
  commitment_created: 'Commitment recorded',
  commitment_completed: 'Commitment completed',
  customer_followup: 'Follow-up',
  product_request: 'Product request',
  product_release: 'Product release',
  system_event: 'System',
};

/** Days overdue, phrased for a table cell. */
export function overdueLabel(businessDaysOverdue: number | null): string {
  if (businessDaysOverdue === null) return '—';
  if (businessDaysOverdue > 0) return `${businessDaysOverdue}d overdue`;
  if (businessDaysOverdue === 0) return 'due today';
  return `${Math.abs(businessDaysOverdue)}d left`;
}

export function pluralise(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** Formats an ISO datetime for a `datetime-local` input. */
export function toDateTimeInput(value: string | Date | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Formats an ISO date for a `date` input. */
export function toDateInput(value: string | Date | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
